import { useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { MdClose, MdCloudDownload, MdSearch, MdCheckCircle } from 'react-icons/md';
import { authenticatedFetch } from '../../../lib/api';
import { useToast } from '../../../components/shared/Toast';
import { Pagination } from '../../../components/shared/Pagination';
import { cn } from '../../../lib/utils';
import { ClearFiltersButton, PageSkeleton, erpPageBtnPrimaryClass, erpPageBtnSecondaryClass } from '@euroasiann/shared/ui';
import { useDebouncedSearch } from '@euroasiann/shared';
import { pmOverlayClass, pmDialogClass, pmFieldClass } from '../port-management.ui';

interface UnlocodeBrowserProps {
  onClose: () => void;
}

function normalizePortCode(port: { code?: string; unlocode?: string }): string {
  return String(port.code || port.unlocode || '').trim().toUpperCase();
}

export function UnlocodeBrowser({ onClose }: UnlocodeBrowserProps) {
  const [page, setPage] = useState(1);
  const {
    inputValue: search,
    submittedValue: submittedSearch,
    setInputValue: setSearch,
    clear: clearSearch,
    onKeyDown: onSearchKeyDown,
  } = useDebouncedSearch('', {
    onCommit: () => setPage(1),
  });
  const {
    inputValue: country,
    submittedValue: submittedCountry,
    setInputValue: setCountryInput,
    clear: clearCountry,
    onKeyDown: onCountryKeyDown,
  } = useDebouncedSearch('', {
    minChars: 2,
    onCommit: () => setPage(1),
  });
  const [selectedCodes, setSelectedCodes] = useState<string[]>([]);
  const limit = 10;
  const { success, error } = useToast();
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['unlocode-reference', submittedSearch, submittedCountry, page],
    queryFn: async ({ signal }) => {
      const params = new URLSearchParams();
      if (submittedSearch) params.append('search', submittedSearch);
      if (submittedCountry) params.append('country', submittedCountry);
      params.append('page', page.toString());
      params.append('limit', limit.toString());
      const res = await authenticatedFetch(`/api/v1/ports/reference/unlocode?${params}`, { signal });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Failed to search UN/LOCODE');
      return json;
    },
  });

  const ports = data?.data || [];
  const meta = data?.meta || { total: 0, page: 1, limit: 10 };
  const totalPages = Math.ceil(meta.total / meta.limit);
  const selectedSet = new Set(selectedCodes);

  const syncMutation = useMutation({
    mutationFn: async (codes: string[]) => {
      const res = await authenticatedFetch('/api/v1/ports/sync/unlocode', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ codes, includeChargeTemplates: true }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Sync failed');
      return json.data;
    },
    onSuccess: (result) => {
      success(`Imported ${result.created} ports, updated ${result.updated}`);
      queryClient.invalidateQueries({ queryKey: ['ports'] });
      queryClient.invalidateQueries({ queryKey: ['unlocode-reference'] });
      setSelectedCodes([]);
    },
    onError: (err: Error) => error(err.message),
  });

  const toggleSelect = useCallback((code: string) => {
    if (!code) return;
    setSelectedCodes((prev) =>
      prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code]
    );
  }, []);

  const selectAllOnPage = () => {
    const codes = ports
      .filter((p: { inRegistry?: boolean }) => !p.inRegistry)
      .map((p: { code?: string; unlocode?: string }) => normalizePortCode(p))
      .filter(Boolean);
    setSelectedCodes(codes);
  };

  const dialog = (
    <div className={`${pmOverlayClass} flex items-center justify-center p-4`} onClick={onClose}>
      <div className={`${pmDialogClass} max-w-4xl`} onClick={(e) => e.stopPropagation()}>
        <div className="flex shrink-0 items-start justify-between gap-3 border-b border-[hsl(var(--border))] px-6 py-4">
          <div className="min-w-0">
            <h2 className="text-xl font-semibold text-[hsl(var(--foreground))]">Global Port Registry (UN/LOCODE)</h2>
            <p className="mt-0.5 text-sm text-[hsl(var(--muted-foreground))]">
              {meta.total?.toLocaleString() ?? '—'} seaports · UNECE reference data · charges are configured locally
            </p>
            {selectedCodes.length > 0 && (
              <p className="mt-1 text-sm font-medium text-[#0088cc]">
                {selectedCodes.length} port{selectedCodes.length !== 1 ? 's' : ''} selected
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="shrink-0 rounded-lg p-1.5 text-[hsl(var(--muted-foreground))] hover:bg-[hsl(var(--muted))]"
            aria-label="Close"
          >
            <MdClose className="h-5 w-5" />
          </button>
        </div>

        <div className="flex shrink-0 flex-wrap items-center gap-3 border-b border-[hsl(var(--border))] px-6 py-3">
          <div className="relative min-w-[12rem] flex-1">
            <MdSearch className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[hsl(var(--muted-foreground))]" />
            <input
              type="text"
              placeholder="Search port name or UN/LOCODE"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={onSearchKeyDown}
              className={`${pmFieldClass} pl-9`}
            />
          </div>
          <input
            type="text"
            placeholder="IN"
            value={country}
            onChange={(e) => setCountryInput(e.target.value.toUpperCase())}
            onKeyDown={onCountryKeyDown}
            maxLength={2}
            className={`${pmFieldClass} w-20 font-mono uppercase`}
          />
          <ClearFiltersButton
            visible={!!search.trim() || !!country.trim()}
            onClick={() => {
              clearSearch();
              clearCountry();
              setPage(1);
            }}
            variant="toolbar"
          />
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-6 py-4">
          {isLoading ? (
            <PageSkeleton variant="panel" />
          ) : ports.length === 0 ? (
            <p className="py-12 text-center text-sm text-[hsl(var(--muted-foreground))]">No ports found — try a different search</p>
          ) : (
            <div className="space-y-2">
              {ports.map((port: any, index: number) => {
                const portCode = normalizePortCode(port);
                const isSelected = portCode ? selectedSet.has(portCode) : false;
                const isInRegistry = Boolean(port.inRegistry);

                return (
                  <button
                    key={portCode || `port-${index}`}
                    type="button"
                    disabled={!portCode || isInRegistry}
                    aria-pressed={isSelected}
                    onClick={() => toggleSelect(portCode)}
                    className={cn(
                      'flex w-full items-center gap-3 rounded-lg border p-3 text-left transition-colors',
                      !portCode || isInRegistry
                        ? 'cursor-not-allowed border-emerald-500/30 bg-emerald-500/10 opacity-80'
                        : isSelected
                          ? 'border-[#0088cc] bg-[#0088cc]/10'
                          : 'cursor-pointer border-[hsl(var(--border))] bg-[hsl(var(--background))] hover:border-[#0088cc]/40 hover:bg-[hsl(var(--muted))]/40'
                    )}
                  >
                    <span
                      className={cn(
                        'flex h-4 w-4 shrink-0 items-center justify-center rounded border transition-colors',
                        isSelected
                          ? 'border-[#0088cc] bg-[#0088cc] text-white'
                          : 'border-[hsl(var(--border))] bg-[hsl(var(--card))]'
                      )}
                      aria-hidden
                    >
                      {isSelected && <MdCheckCircle className="h-3 w-3" />}
                    </span>
                    <div className="min-w-0 flex-1 pointer-events-none">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-sm font-medium text-[hsl(var(--foreground))]">{port.name}</span>
                        <span className="font-mono text-xs text-[#0088cc]">{portCode}</span>
                        {isInRegistry && (
                          <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-600 dark:text-emerald-400">
                            <MdCheckCircle className="h-3 w-3" /> In registry
                          </span>
                        )}
                      </div>
                      <div className="mt-0.5 text-xs text-[hsl(var(--muted-foreground))]">
                        {port.country}
                        {(port.lat != null || port.latitude != null) && (
                          <> · {(port.lat ?? port.latitude)?.toFixed?.(4)}, {(port.lng ?? port.longitude)?.toFixed?.(4)}</>
                        )}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <div className="shrink-0 space-y-3 border-t border-[hsl(var(--border))] px-6 py-4">
          {meta.total > 0 && (
            <Pagination
              currentPage={page}
              totalPages={totalPages}
              onPageChange={setPage}
              itemsPerPage={limit}
              totalItems={meta.total}
              onItemsPerPageChange={() => {}}
            />
          )}
          <div className="flex flex-wrap items-center justify-between gap-3">
            <button
              type="button"
              onClick={selectAllOnPage}
              className="text-sm font-medium text-[#0088cc] hover:underline"
            >
              Select all importable on page
            </button>
            <div className="flex gap-2">
              <button type="button" onClick={onClose} className={erpPageBtnSecondaryClass}>
                Cancel
              </button>
              <button
                type="button"
                onClick={() => syncMutation.mutate(selectedCodes)}
                disabled={selectedCodes.length === 0 || syncMutation.isPending}
                className={erpPageBtnPrimaryClass}
              >
                <MdCloudDownload className="h-4 w-4" />
                {syncMutation.isPending ? 'Importing...' : `Import ${selectedCodes.length} port${selectedCodes.length !== 1 ? 's' : ''}`}
              </button>
            </div>
          </div>
          <p className="text-center text-xs text-[hsl(var(--muted-foreground))]">
            Click a row to select it. Ports already in your registry cannot be selected again.
          </p>
        </div>
      </div>
    </div>
  );

  return createPortal(dialog, document.body);
}
